import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plugs, Plus, Trash, PaperPlaneTilt, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

var ALL_EVENTS = ["conversion", "click", "payout", "fraud_alert", "affiliate_signup", "offer_created", "schedule_fired", "commission_earned"];

export default function WebhookManagement() {
  var [configs, setConfigs] = useState([]);
  var [logs, setLogs] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [form, setForm] = useState({ url: '', events: ALL_EVENTS.slice() });

  var fetchData = function () {
    Promise.all([api.get('/webhooks/configs'), api.get('/webhooks/logs')])
      .then(function (r) { setConfigs(r[0].data); setLogs(r[1].data); })
      .catch(console.error).finally(function () { setLoading(false); });
  };
  useEffect(function () { fetchData(); }, []);

  var create = async function (e) {
    e.preventDefault();
    try {
      await api.post('/webhooks/configs', form);
      toast.success('Webhook created');
      setDialogOpen(false);
      setForm({ url: '', events: ALL_EVENTS.slice() });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  var testWebhook = async function (id) {
    try {
      var r = await api.post('/webhooks/test/' + id);
      toast.success('Test fired: ' + r.data.status);
      fetchData();
    } catch (_e) { toast.error('Test failed'); }
  };

  var deleteWebhook = async function (id) {
    try { await api.delete('/webhooks/configs/' + id); toast.success('Deleted'); fetchData(); } catch (_e) { toast.error('Failed'); }
  };

  var toggleEvent = function (evt) {
    setForm(function (p) {
      var evts = p.events.includes(evt) ? p.events.filter(function (e) { return e !== evt; }) : p.events.concat([evt]);
      return Object.assign({}, p, { events: evts });
    });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="webhook-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Plugs size={32} className="text-blue-400" weight="duotone" /> Webhook Management
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Configure webhooks for real-time event delivery to third-party systems</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="create-webhook-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><Plus size={16} /> New Webhook</Button></DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-white">Create Webhook</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div><Label className="text-zinc-300">Endpoint URL</Label><Input data-testid="webhook-url" value={form.url} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { url: e.target.value }); }); }} required placeholder="https://your-server.com/webhook" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div>
                <Label className="text-zinc-300 mb-2 block">Events to Subscribe</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_EVENTS.map(function (evt) {
                    var active = form.events.includes(evt);
                    return (
                      <button key={evt} type="button" data-testid={'event-' + evt} onClick={function () { toggleEvent(evt); }}
                        className={'px-3 py-2 rounded-lg text-xs border transition-all ' + (active ? 'bg-blue-600/15 border-blue-500/30 text-blue-400' : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-zinc-700')}>
                        {evt.replace('_', ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button data-testid="submit-webhook" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">Create Webhook</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Webhook Endpoints</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">ID</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">URL</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Events</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Deliveries</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {configs.map(function (c, i) {
                return (
                  <TableRow key={i} className="border-zinc-800/50" data-testid={'webhook-row-' + i}>
                    <TableCell className="text-zinc-500 font-mono text-xs">{c.webhook_id}</TableCell>
                    <TableCell className="text-blue-400 text-xs max-w-[200px] truncate font-mono">{c.url}</TableCell>
                    <TableCell><div className="flex flex-wrap gap-1">{(c.events || []).slice(0, 3).map(function (e) { return <Badge key={e} className="bg-zinc-800 text-zinc-400 text-[9px]">{e}</Badge>; })}{c.events?.length > 3 && <Badge className="bg-zinc-800 text-zinc-500 text-[9px]">+{c.events.length - 3}</Badge>}</div></TableCell>
                    <TableCell className="text-zinc-300">{c.total_deliveries || 0}</TableCell>
                    <TableCell><Badge className={c.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 text-[10px]' : 'bg-zinc-700/30 text-zinc-400 text-[10px]'}>{c.status}</Badge></TableCell>
                    <TableCell><div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={function () { testWebhook(c.webhook_id); }} data-testid={'test-webhook-' + i} className="text-zinc-400 hover:text-blue-400 text-xs gap-1"><PaperPlaneTilt size={12} /> Test</Button>
                      <Button variant="ghost" size="sm" onClick={function () { deleteWebhook(c.webhook_id); }} className="text-zinc-400 hover:text-red-400"><Trash size={14} /></Button>
                    </div></TableCell>
                  </TableRow>
                );
              })}
              {configs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No webhooks configured.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white flex items-center gap-2"><Lightning size={20} className="text-amber-400" /> Delivery Log</CardTitle></CardHeader>
        <CardContent>
          <div className="max-h-64 overflow-y-auto">
            <Table>
              <TableHeader><TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Event</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">URL</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Code</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Time</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {logs.slice(0, 20).map(function (l, i) {
                  return (
                    <TableRow key={i} className="border-zinc-800/50">
                      <TableCell><Badge className="bg-blue-500/10 text-blue-400 text-[10px]">{l.event}</Badge></TableCell>
                      <TableCell className="text-zinc-400 text-xs max-w-[200px] truncate font-mono">{l.url}</TableCell>
                      <TableCell><Badge className={l.status === 'delivered' ? 'bg-emerald-500/10 text-emerald-400 text-[10px]' : 'bg-red-500/10 text-red-400 text-[10px]'}>{l.status}</Badge></TableCell>
                      <TableCell className="text-zinc-300 text-xs">{l.response_code || '-'}</TableCell>
                      <TableCell className="text-zinc-500 text-xs">{l.created_at ? new Date(l.created_at).toLocaleTimeString() : '-'}</TableCell>
                    </TableRow>
                  );
                })}
                {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-6">No webhook deliveries yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
